import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingAPI } from '../../api/bookings';
import { format } from 'date-fns';
import ChatWindow from '../chat/ChatWindow';
import { useAuth } from '../../contexts/AuthContext';

const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    booking_date: '',
    start_time: ''
  });

  useEffect(() => {
    loadBooking();
  }, [id]);

  const loadBooking = async () => {
    try {
      const response = await bookingAPI.getById(id);
      setBooking(response.data.data);
      setRescheduleData({
        booking_date: response.data.data.booking_date,
        start_time: response.data.data.start_time
      });
    } catch (error) {
      console.error('Failed to load booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel this booking?')) {
      try {
        await bookingAPI.cancel(id, 'Cancelled by customer');
        alert('Booking cancelled successfully');
        navigate('/bookings');
      } catch (error) {
        console.error('Failed to cancel:', error);
      }
    }
  };

  const handleReschedule = async () => {
    try {
      await bookingAPI.reschedule(id, rescheduleData);
      alert('Booking rescheduled successfully');
      setShowReschedule(false);
      loadBooking();
    } catch (error) {
      console.error('Failed to reschedule:', error);
    }
  };

  const handleStartBooking = async () => {
    try {
      const response = await bookingAPI.start(id);
      setBooking(response.data.data);
    } catch (error) {
      console.error('Failed to start booking:', error);
      alert(error?.response?.data?.message || 'Failed to start booking');
    }
  };

  const handleCompleteBooking = async () => {
    try {
      const response = await bookingAPI.complete(id);
      setBooking(response.data.data);
    } catch (error) {
      console.error('Failed to complete booking:', error);
      alert(error?.response?.data?.message || 'Failed to complete booking');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;
  if (!booking) return <div className="text-center py-8">Booking not found</div>;
  const currencySymbol = booking.currency_symbol || '$';
  const bookingDateStr = booking.booking_date_local
    ? booking.booking_date_local
    : (typeof booking.booking_date === 'string'
        ? booking.booking_date.split('T')[0]
        : format(new Date(booking.booking_date), 'yyyy-MM-dd'));

  const bookingTimeStr = booking.end_time_local
    ? `${booking.start_time_local} - ${booking.end_time_local}`
    : (booking.start_time_local || booking.start_time);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold">Booking Details</h1>
              <p className="text-gray-600">Booking #{booking.id}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(booking.status)}`}>
              {booking.status}
            </span>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Service</p>
              <p className="font-medium">{booking.service_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Price</p>
              <p className="font-medium">{currencySymbol}{booking.total_price}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Date</p>
              <p className="font-medium">{format(new Date(`${bookingDateStr}T00:00:00`), 'MMMM dd, yyyy')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Time</p>
              <p className="font-medium">{bookingTimeStr}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Staff</p>
              <p className="font-medium">{booking.staff_name || 'Not assigned'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Business</p>
              <p className="font-medium">{booking.business_name}</p>
            </div>
          </div>

          {booking.notes && (
            <div>
              <p className="text-sm text-gray-500">Notes</p>
              <p className="text-gray-700">{booking.notes}</p>
            </div>
          )}

          {booking.status === 'cancelled' && booking.cancellation_reason && (
            <div>
              <p className="text-sm text-gray-500">Cancellation Reason</p>
              <p className="text-gray-700">{booking.cancellation_reason}</p>
            </div>
          )}

          {booking.status !== 'cancelled' && booking.status !== 'completed' && booking.status !== 'in_progress' && (
            <div className="flex space-x-4 pt-4 border-t">
              <button
                onClick={() => setShowReschedule(true)}
                className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
              >
                Reschedule
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Cancel Booking
              </button>
            </div>
          )}

          {(user?.role === 'business_owner' || user?.role === 'staff') && booking.status === 'confirmed' && (
            <div className="flex space-x-4 pt-4 border-t">
              <button
                onClick={handleStartBooking}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Start Booking
              </button>
            </div>
          )}

          {(user?.role === 'business_owner' || user?.role === 'staff') && booking.status === 'in_progress' && (
            <div className="flex space-x-4 pt-4 border-t">
              <button
                onClick={handleCompleteBooking}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Complete Booking
              </button>
            </div>
          )}
        </div>
      </div>

      {['confirmed', 'in_progress', 'completed'].includes(booking.status) && (
        <div className="mt-8">
          <h2 className="text-xl font-bold mb-4">Chat</h2>
          <ChatWindow bookingId={id} />
        </div>
      )}

      {showReschedule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">Reschedule Booking</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">New Date</label>
                <input
                  type="date"
                  value={rescheduleData.booking_date}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, booking_date: e.target.value })}
                  className="w-full input"
                  min={format(new Date(), 'yyyy-MM-dd')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">New Time</label>
                <input
                  type="time"
                  value={rescheduleData.start_time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, start_time: e.target.value })}
                  className="w-full input"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button onClick={() => setShowReschedule(false)} className="btn-secondary">
                  Cancel
                </button>
                <button onClick={handleReschedule} className="btn-primary">
                  Confirm Reschedule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingDetails;
